import type { FormStateValue } from '@de/providers/DataSourceManager.types';
import { formStateValueToKeys } from '@de/utils/form-state';

export interface HostSeedReconcile {
  /** Replacement form value. Absent when every seeded token matched. */
  readonly next: FormStateValue | undefined;
  readonly dropped: readonly string[];
}

/**
 * Drop host-seeded tokens that match no option.
 *
 * Each comma-separated (or list) token is checked on its own. Matching
 * tokens stay, in order, so one stale facet does not clear the rest.
 * An empty result is `''` for a string seed and `[]` for a list seed —
 * the same cleared shapes the form-state reducer already treats as unset.
 */
export function reconcileHostSeededOptions(input: {
  readonly value: FormStateValue | undefined;
  readonly optionValues: readonly string[];
}): HostSeedReconcile {
  const keys = formStateValueToKeys(input.value);
  if (keys.length === 0) return { next: undefined, dropped: [] };

  const allowed = new Set(input.optionValues);
  const kept: string[] = [];
  const dropped: string[] = [];
  for (const key of keys) {
    if (allowed.has(key)) kept.push(key);
    else dropped.push(key);
  }
  if (dropped.length === 0) return { next: undefined, dropped: [] };
  if (kept.length === 0) {
    return {
      next: Array.isArray(input.value) ? [] : '',
      dropped,
    };
  }
  if (Array.isArray(input.value)) return { next: kept, dropped };
  return { next: kept.join(','), dropped };
}
